import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { ProductFormData } from "../../../shared/schemas/product.schema";
import { buildClonedProductFormData } from "../../../shared/schemas/product-clone.helper";
import { trpcClient } from "@/lib/trpc";
import ProductForm from "./ProductForm";
import { toast } from "sonner";
import { useState, useEffect } from "react";
import { ComboboxFK } from "@/components/shared/ComboboxFK";
import { useProductLookup } from "@/hooks/useFKLookup";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";

export default function ProductCreate() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const productLookup = useProductLookup();
  const [sourceId, setSourceId] = useState("");
  const [sourceData, setSourceData] = useState<Partial<ProductFormData> | null>(
    null,
  );
  const [isLoadingSource, setIsLoadingSource] = useState(false);

  useEffect(() => {
    const queryId = new URLSearchParams(window.location.search).get("sourceId");
    if (queryId && !sourceId) {
      setSourceId(queryId);
      loadSource(queryId);
    }
  }, []);

  const loadSource = async (id: string) => {
    try {
      setIsLoadingSource(true);
      const data: any = await trpcClient.products.getById.query({ id });
      if (data) {
        const cloned = buildClonedProductFormData(data);
        setSourceData(cloned);
        // Tự động focus vào ô Tên sản phẩm sau khi form mount
        setTimeout(() => {
          const input = document.querySelector('input[name="productName"]') as HTMLInputElement | null;
          if (input) {
            input.focus();
            input.select();
          }
        }, 150);
      }
    } catch (e) {
      toast.error("Lỗi tải sản phẩm tham khảo");
    } finally {
      setIsLoadingSource(false);
    }
  };

  const handleSelectSource = (id: string) => {
    setSourceId(id);
    if (id) {
      loadSource(id);
    } else {
      setSourceData(null);
    }
  };

  const handleSubmit = async (data: ProductFormData) => {
    try {
      await trpcClient.products.create.mutate(data as any);

      await queryClient.invalidateQueries({ queryKey: ["products"] });
      await queryClient.invalidateQueries({ queryKey: ["products-lookup"] });
      if (window.history.length > 2) window.history.back();
      else navigate({ to: "/products" });
      return true;
    } catch (err: any) {
      const msg = err?.message || "Tạo sản phẩm thất bại";
      // Hiện lỗi cụ thể cho user thay vì fallback im lặng
      toast.error(msg);
      throw err;
    }
  };

  return (
    <StandardDetailLayout
      title="Tạo Sản Phẩm"
      breadcrumbs={[{ label: "Sản phẩm", to: "/products" }]}
      showContextPanel={false}
    >
      <div className="w-full space-y-4 px-0 py-4 animate-in fade-in duration-300">
        {isLoadingSource ? (
          <div className="flex items-center justify-center p-12 bg-white rounded-lg border">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
            <span className="ml-3 text-slate-700">
              Đang tải dữ liệu tham khảo...
            </span>
          </div>
        ) : (
          <ProductForm
            key={sourceId || "base"}
            defaultValues={sourceData || undefined}
            onSubmit={handleSubmit}
            onCancel={() =>
              window.history.length > 2
                ? window.history.back()
                : navigate({ to: "/products" })
            }
            submitLabel="Tạo mới"
          />
        )}
      </div>
    </StandardDetailLayout>
  );
}
